/**
 * Die Subnavigation eines Produktes.
 *
 * EIN MUSTER, KEINE SONDERLOESUNG. Diese Komponente kennt keinen einzigen
 * Bereichsnamen: Reihenfolge und Umfang kommen aus `PRODUCT_SECTIONS`, die
 * Beschriftung aus dem Woerterbuch, die Sichtbarkeit aus den Faehigkeiten des
 * angemeldeten Nutzers. Ein weiterer Bereich ist deshalb eine Zeile in der
 * Deklaration -- hier ist dafuer nichts zu aendern.
 *
 * WAS SICH GEGENUEBER V1 AENDERT
 *
 * V1 hatte eine senkrechte Schiene rechts. Sie war immer sichtbar, kostete
 * aber dauerhaft rund 230 px Breite -- auf einem Tablet Landscape genau die
 * Breite, die der technischen Uebersicht fuer ihre dritte Saeule fehlte. Und
 * sie war eine Loesung fuer DIESE Seite, nicht ein Muster fuer HOMEBASE.
 *
 * Jetzt: eine flache waagerechte Leiste unter dem Produktkopf. Sie kostet
 * eine Zeilenhoehe, sie liest sich in der Reihenfolge, in der man arbeitet,
 * und sie ist auf jeder Seite wiederverwendbar, die ein Objekt in Bereiche
 * gliedert.
 *
 * KEINE ZUSTANDSABZEICHEN. Ein roter Punkt an „Einkauf" waere ein Warnhinweis
 * in der kundensicheren Erstansicht -- genau das, was dieser Block aus der
 * Uebersicht entfernt. Was offen ist, steht IM Bereich.
 */
import type { Capability, ProductSectionCode } from "@homebase/shared";
import { PRODUCT_SECTIONS, SECTION_CAPABILITY } from "@homebase/shared";
import { useI18n } from "../i18n/index.tsx";
import "./ProductSubnav.css";

/**
 * Die Bereiche, die dieser Nutzer ueberhaupt sehen soll.
 *
 * Ein ausgegrauter Reiter ist bereits eine Auskunft: er sagt, dass es hier
 * etwas gibt. Fehlt der Reiter, gibt es fuer diesen Nutzer diesen Bereich
 * nicht -- das ist die Vorgabe aus Abschnitt 6 des Auftrags.
 *
 * ACHTUNG: das ist Ordnung in der Oberflaeche, KEIN Schutz. Die Daten selbst
 * schuetzt der Server, und er tut es fuer den Einkauf heute nicht (siehe
 * `SECTION_CAPABILITY`).
 */
export function visibleSections(
  capabilities: readonly Capability[],
): readonly ProductSectionCode[] {
  return PRODUCT_SECTIONS.filter((s) => {
    const noetig = SECTION_CAPABILITY[s];
    return noetig === undefined || capabilities.includes(noetig);
  });
}

export function ProductSubnav<T extends string = ProductSectionCode>({
  sections, current, onSelect, action, prefix = "work",
}: {
  /** Bereits gefiltert -- die Komponente entscheidet nicht ueber Rechte. */
  sections: readonly T[];
  current: T;
  onSelect: (s: T) => void;
  /**
   * Der Wortstamm der Beschriftungen.
   *
   * Diese Leiste gliedert seit dem Lieferantenportal ZWEI Objekte: die
   * Produktakte („work.*") und die Lieferantenakte („sup.*"). Sie kennt
   * weiterhin keinen einzigen Bereichsnamen -- nur, aus welchem Teil des
   * Woerterbuchs die Beschriftungen kommen. Eine zweite, fast gleiche
   * Leiste waere die teurere Antwort auf dieselbe Frage.
   */
  prefix?: string;
  /**
   * EINE Handlung am rechten Ende der Leiste -- heute der Stift, der den
   * Bearbeitungsmodus eines Bereichs einschaltet.
   *
   * Sie steht als `ReactNode` hier und nicht als Bereichsliste mit
   * Bleistiftrecht: diese Komponente kennt keinen Bereichsnamen, und sie soll
   * auch keinen kennen. WELCHER Bereich eine Bearbeitung anbietet und was sie
   * dort bedeutet, entscheidet der Aufrufer -- hier ist nur der Platz dafuer.
   *
   * Fehlt sie, fehlt sie: eine ausgegraute Schaltflaeche in fuenf Bereichen,
   * in denen sie nichts tut, waere eine Ankuendigung und keine Bedienung.
   */
  action?: React.ReactNode;
}): React.JSX.Element {
  const i18n = useI18n();
  return (
    <nav className="subnav" aria-label={i18n.t("work.aria")}>
      {/*
        * `aria-current="page"` statt einer ARIA-Tabliste: die Bereiche sind
        * Navigationsziele eines Objekts, keine Karteikarten in einem Dialog.
        * Ein Screenreader soll „Einkauf, aktuelle Seite" vorlesen.
        */}
      {sections.map((code) => {
        const aktiv = code === current;
        return (
          <button key={code} type="button"
                  className={aktiv ? "subnav__item subnav__item--on" : "subnav__item"}
                  aria-current={aktiv ? "page" : undefined}
                  onClick={() => onSelect(code)}>
            {i18n.tCode(prefix, code, code)}
          </button>
        );
      })}
      {action && <span className="subnav__action">{action}</span>}
    </nav>
  );
}
